/* eslint-disable react/prop-types */ // TODO 5816 fixa ts i efterhand vid review
import React, { memo, useMemo, useEffect } from 'react'

import { useAppSelector } from '../../hooks'
import { getBaseUrls } from '../../helpers/getBaseUrls'

declare global {
  interface Window {
    productConfiguratorScriptsLoaded: boolean | undefined
    productConfiguratorAdapters: {
      init: () => Promise<void>
    }
    productConfiguratorController: {
      init: () => Promise<void>
    }
  }
}

interface Props {
  lfcItem: any
  pricingInfo: string
  initialChosenValues: number[]
  initialConfigResult: any
  dynamakerItemId: string
  onChange: ({
    selectedItemId,
    chosenValues,
    configResult,
  }: {
    selectedItemId: string
    chosenValues: number[]
    configResult: any
  }) => void
}

const MallisConfigurator: React.FC<Props> = memo(function MallisConfigurator({
  lfcItem,
  pricingInfo,
  initialChosenValues,
  initialConfigResult,
  dynamakerItemId,
  onChange,
}) {
  const { languageCode, marketCode, productConfiguratorAdaptersScriptUrl, productConfiguratorControllerScriptUrl } =
    useAppSelector((state) => state.init)

  const countryCode = languageCode.slice(-2)

  // I väntan på uppdaterad datastruktur från LFC API
  const localizedDimensions = useMemo(
    () =>
      lfcItem!.dimensions.map((dim: any) => ({
        ...dim,
        dimensionName: dim[`dimensionName${countryCode}`],
        hintDescription: dim[`hintDescription${countryCode}`],
        values: dim.values.map((val: any) => ({
          ...val,
          valueName: val[`valueName${countryCode}`],
        })),
        hidden: lfcItem!.presets[0].configResult.configProducts[0].editorial.dimsHiddenFromMarkets.some(
          ({ hiddenDimId }: { hiddenDimId: number }) => hiddenDimId === dim.dimensionId
        ),
      })),
    [lfcItem]
  )

  useEffect(() => {
    const handleConfigResult = (event: Event) => {
      const {
        dynamakerItemId,
        chosenValues,
        configResult,
      }: { dynamakerItemId: string; chosenValues: number[]; configResult: any } = (event as CustomEvent).detail

      onChange({ selectedItemId: dynamakerItemId, chosenValues, configResult })
    }

    window.addEventListener('mallis-config-update', handleConfigResult)

    let shouldInit = true

    async function ensureLoadedAndInit() {
      try {
        // Load scripts once
        if (!window.productConfiguratorScriptsLoaded) {
          const productConfiguratorAdaptersScript = document.createElement('script')
          const productConfiguratorControllerScript = document.createElement('script')

          productConfiguratorAdaptersScript.type = 'module'
          productConfiguratorControllerScript.type = 'module'

          productConfiguratorAdaptersScript.src = productConfiguratorAdaptersScriptUrl
          productConfiguratorControllerScript.src = productConfiguratorControllerScriptUrl

          document.body.appendChild(productConfiguratorAdaptersScript)
          document.body.appendChild(productConfiguratorControllerScript)

          await Promise.all(
            [productConfiguratorAdaptersScript, productConfiguratorControllerScript].map(
              (script) =>
                new Promise((res, rej) => {
                  script.onload = res
                  script.onerror = rej
                })
            )
          )

          window.productConfiguratorScriptsLoaded = true
        }

        if (shouldInit) {
          window.productConfiguratorAdapters
            .init()
            .then(() => {
              window.productConfiguratorController.init()
            })
            .catch((error) => {
              console.error('Error initializing product configurator adapters:', error)
            })
        }
      } catch (error) {
        console.error('Error in MallisConfigurator ensureLoadedAndInit:', error)
      }
    }

    ensureLoadedAndInit()

    return () => {
      window.removeEventListener('mallis-config-update', handleConfigResult)

      shouldInit = false
      // No need to remove module script tag here – modules can't be unloaded
    }
  })

  const {lfcApiBaseUrl, internalDataBaseUrl} = getBaseUrls()
  return (
    <>
      <div
        style={{ display: 'none' }}
        id="product-configurator-adapters-data"
        data-configurator-application="LFC_MALLIS_IN_MOK_CONTEXT"
        data-mok-context-item-id={dynamakerItemId}
        data-lfc-api-base-url={lfcApiBaseUrl}
        data-internal-data-base-url={internalDataBaseUrl}
      />
      <div
        style={{ display: 'none' }}
        id="product-configurator-config-init-params"
        data-lang={languageCode}
        data-market-code={marketCode}
        data-context-id={lfcItem.configuratorContextId}
        data-pricing-info={pricingInfo}
        data-dimensions={JSON.stringify(localizedDimensions)}
        data-constraints-rules={JSON.stringify(lfcItem.constraintsRules)}
        data-chosen-values={JSON.stringify(initialChosenValues)}
        data-config-result={JSON.stringify(initialConfigResult)}
        data-dynamaker-item-id={dynamakerItemId}
      />
      <div
        id="product-configurator-controller-root"
      >
        {[...Array(6)].map((_, index) => (
          <div key={index} className="pcc-skeleton" />
        ))}
      </div>
    </>
  )
})

export default MallisConfigurator
